import {
  Control,
  FieldArrayWithId,
  FieldErrors,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
} from 'react-hook-form';
import { Button } from 'antd';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import { StudentProfileForm } from '../../../constants/types/student-profile-form';
import FormRow from '../../common/input/FormRow';
import TextWinds from '../../common/TextWinds';
import {
  birthdayRules,
  nameRules,
  phoneRules,
} from '../../../constants/validation';
import { Field } from '../../common/form/Field';
import SectionHeader, {
  SectionActionProps,
  SectionBody,
} from '../../common/form/SectionHeader';

/** 영역 3. 보호자 정보. 항목을 추가/삭제할 수 있고 저장 시 배열이 통째로 교체된다. */
const ParentsSection = ({
  control,
  errors,
  section,
  fields,
  append,
  remove,
  className,
  maxParents,
}: {
  control: Control<StudentProfileForm>;
  errors: FieldErrors<StudentProfileForm>;
  section: SectionActionProps;
  /** 바깥 여백은 화면마다 달라서 넘겨받는다. */
  className?: string;
  /** useFieldArray는 StudentDetail에서 관리한다. (취소 시 reset과 함께 다뤄야 하므로) */
  fields: FieldArrayWithId<StudentProfileForm, 'parents', 'id'>[];
  append: UseFieldArrayAppend<StudentProfileForm, 'parents'>;
  remove: UseFieldArrayRemove;
  /**
   * 등록 가능한 보호자 수 제한.
   * API가 배열을 통째로 받더라도, 화면 정책상 이 값을 넘겨 추가할 수 없다.
   * (서버가 실제로 이 개수를 강제하는 것은 아니므로 화면별로 명시해서 넘긴다.)
   */
  maxParents: number;
}) => {
  const isEditing = section.isEditing;

  return (
    <section className={className}>
      <SectionHeader title="보호자 정보" {...section} />
      <SectionBody isOpen={section.isOpen}>
        <div className="mt-5 flex flex-col gap-y-2.5">
          {fields.length === 0 && !isEditing ? (
            <TextWinds type="content_body2" className="m-auto mb-4 text-gray6">
              등록된 보호자가 없습니다
            </TextWinds>
          ) : (
            fields.map((item, index) => {
              const parentErrors = errors.parents?.[index];

              return (
                <div key={item.id} className="rounded bg-gray1 p-4">
                  <div className="h-5 text-right">
                    {isEditing && (
                      <Button
                        type="dashed"
                        className="p-1 text-sm font-bold text-purple5"
                        onClick={() => remove(index)}
                      >
                        삭제하기
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    <Field
                      label="보호자 이름"
                      error={parentErrors?.name?.message}
                    >
                      <FormRow
                        type="text"
                        name={`parents.${index}.name`}
                        control={control}
                        placeholder="보호자 이름 입력"
                        rules={nameRules({
                          label: '보호자 이름',
                          required: true,
                        })}
                        error={!!parentErrors?.name}
                        disabled={!isEditing}
                        autoComplete="off"
                      />
                    </Field>

                    <Field
                      label="보호자 전화번호"
                      error={parentErrors?.phone?.message}
                    >
                      <FormRow
                        type="phone"
                        name={`parents.${index}.phone`}
                        control={control}
                        placeholder="- 없이 번호만 입력"
                        rules={phoneRules({
                          label: '보호자 전화번호',
                          required: true,
                        })}
                        error={!!parentErrors?.phone}
                        disabled={!isEditing}
                        autoComplete="off"
                      />
                    </Field>

                    <Field
                      label="보호자 생년월일"
                      error={parentErrors?.birthday?.message}
                    >
                      <FormRow
                        type="birthday"
                        name={`parents.${index}.birthday`}
                        control={control}
                        rules={birthdayRules({
                          label: '보호자 생년월일',
                          required: true,
                        })}
                        disabled={!isEditing}
                        error={!!parentErrors?.birthday}
                      />
                    </Field>

                    <Field
                      label="보호자 성별"
                      error={parentErrors?.parentType?.message}
                    >
                      <FormRow
                        type="gender"
                        name={`parents.${index}.parentType`}
                        control={control}
                        rules={{
                          required: '보호자 성별을 선택해주세요',
                        }}
                        error={!!parentErrors?.parentType}
                        disabled={!isEditing}
                      />
                    </Field>
                  </div>
                </div>
              );
            })
          )}

          {isEditing && fields.length < maxParents && (
            <Button
              type="dashed"
              className="flex items-center justify-center gap-2 bg-white p-2.5 text-sm font-bold text-purple5"
              icon={<PlusCircleIcon className="mt-1 size-5" />}
              onClick={() =>
                append({
                  name: '',
                  phone: '',
                  parentType: '',
                  birthday: '',
                })
              }
            >
              보호자 추가하기
            </Button>
          )}
        </div>
      </SectionBody>
    </section>
  );
};

export default ParentsSection;
